import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import StepIndicator from '../onboarding/StepIndicator';
import ConnectorGrid from '../onboarding/ConnectorGrid';
import ConnectionForm from '../onboarding/ConnectionForm';
import TableSelector from '../onboarding/TableSelector';
import ProfileStep from '../onboarding/ProfileStep';
import DictionaryStep from '../onboarding/DictionaryStep';
import RulePreview from '../onboarding/RulePreview';
import SaveToProject from './SaveToProject';
import ProfilingLanding from './ProfilingLanding';
import type {
  OnboardingState, ConnectorType, DomainType, TableInfo, SuggestedRule,
  ProfileResult, DictionaryResult,
} from '../../types';

const initialState: OnboardingState = {
  step: 0,
  connectorType: null,
  connection: {},
  connectionTested: false,
  selectedSchema: null,
  selectedTables: [],
  columns: {},
  suggestedRules: [],
  domain: 'default',
  projectName: '',
  groupName: '',
  createAndRunResult: null,
  profileResult: null,
  dictionaryResult: null,
};

const steps = ['Connect', 'Tables', 'Profile', 'Dictionary', 'Rules', 'Save'];

const domains: { value: DomainType; label: string; description: string }[] = [
  { value: 'default', label: 'General', description: 'Universal profiler rules only' },
  { value: 'insurance', label: 'Insurance', description: 'Claims, policies, LOB, SSN rules' },
  { value: 'healthcare', label: 'Healthcare', description: 'Patient, NPI, ICD-10, admission rules' },
  { value: 'financial', label: 'Financial', description: 'Financial data quality weights' },
  { value: 'legal', label: 'Legal', description: 'Legal/provenance-focused rules' },
];

const SourceProfilingWizard: React.FC = () => {
  const [showWizard, setShowWizard] = useState(false);
  const [state, setState] = useState<OnboardingState>(initialState);

  const setStep = (step: number) => setState((s) => ({ ...s, step }));

  const setConnectorType = (connectorType: ConnectorType) =>
    setState((s) => ({
      ...s,
      connectorType,
      connection: { connector_type: connectorType },
      // Stay on step 0 — ConnectionForm renders within the same "Connect" step
    }));

  const setConnection = (connection: OnboardingState['connection'], tested: boolean) =>
    setState((s) => ({ ...s, connection, connectionTested: tested }));

  const setSchema = (schema: string) =>
    setState((s) => ({ ...s, selectedSchema: schema }));

  const setTables = (tables: TableInfo[]) =>
    setState((s) => ({ ...s, selectedTables: tables }));

  const setColumns = (cols: Record<string, any>) =>
    setState((s) => ({ ...s, columns: cols }));

  const setRules = (rules: SuggestedRule[]) =>
    setState((s) => ({ ...s, suggestedRules: rules }));

  const setDomain = (domain: DomainType) =>
    setState((s) => ({ ...s, domain, suggestedRules: [] }));

  const setProjectName = (name: string) =>
    setState((s) => ({ ...s, projectName: name }));

  const setGroupName = (name: string) =>
    setState((s) => ({ ...s, groupName: name }));

  const setProfileResult = (profileResult: ProfileResult) =>
    setState((s) => ({ ...s, profileResult }));

  const setDictionaryResult = (dictionaryResult: DictionaryResult) =>
    setState((s) => ({ ...s, dictionaryResult }));

  const next = () => setStep(state.step + 1);
  const back = () => setStep(Math.max(0, state.step - 1));
  const reset = () => {
    setState(initialState);
    setShowWizard(false);
  };

  if (!showWizard) {
    return (
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-cargo-text">Data Profiling</h1>
          <p className="text-cargo-muted mt-1">
            View past profiling assessments or start a new one
          </p>
        </div>
        <ProfilingLanding onNewSession={() => setShowWizard(true)} />
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={reset}
          className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel transition-colors"
          title="Back to history"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-cargo-text">Data Profiling</h1>
          <p className="text-cargo-muted mt-1">
            Connect, profile, generate dictionary, and auto-generate data quality rules
          </p>
        </div>
      </div>

      <StepIndicator steps={steps} current={state.step} />

      <div className="mt-8">
        {/* Step 0: Connector + Connection (merged as "Connect") */}
        {state.step === 0 && !state.connectorType && (
          <ConnectorGrid onSelect={setConnectorType} selected={state.connectorType} />
        )}
        {state.step === 0 && state.connectorType && (
          <ConnectionForm
            state={state}
            setConnection={setConnection}
            onNext={next}
            onBack={() => setState((s) => ({ ...s, connectorType: null, connection: {} }))}
          />
        )}

        {/* Step 1: Select Schema + Tables */}
        {state.step === 1 && (
          <TableSelector
            state={state}
            setSchema={setSchema}
            setTables={setTables}
            setColumns={setColumns}
            onNext={next}
            onBack={back}
          />
        )}

        {/* Step 2: Profile The Source Table */}
        {state.step === 2 && (
          <ProfileStep
            state={state}
            setProfileResult={setProfileResult}
            onNext={next}
            onBack={back}
          />
        )}

        {/* Step 3: Generate Dictionary */}
        {state.step === 3 && (
          <DictionaryStep
            state={state}
            setDictionaryResult={setDictionaryResult}
            onNext={next}
            onBack={back}
          />
        )}

        {/* Step 4: Generate DQ Rules */}
        {state.step === 4 && (
          <RulePreview
            state={state}
            setRules={setRules}
            setDomain={setDomain}
            domains={domains}
            onNext={next}
            onBack={back}
          />
        )}

        {/* Step 5: Save DQ Rules - Project */}
        {state.step === 5 && (
          <SaveToProject
            state={state}
            setProjectName={setProjectName}
            setGroupName={setGroupName}
            onBack={back}
          />
        )}
      </div>
    </div>
  );
};

export default SourceProfilingWizard;
